import React, { useState, useEffect } from 'react';
import { 
  getSkillCategories,
  createSkillCategory,
  updateSkillCategory,
  deleteSkillCategory,
  addSkill,
  updateSkill,
  deleteSkill
} from '../../services/profile';
import { FaEdit, FaTrashAlt, FaPlus, FaTimes, FaSave, FaLightbulb } from 'react-icons/fa';
import './Skills.css';

const Skills = ({profileSection, profileData, setCompletion}) => {
  const [skillCategories, setSkillCategories] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // State for new/editing skill category
  const [skillForm, setSkillForm] = useState({
    category_name: '',
    skills: ''
  });

  // Fetch skill categories on component mount
  useEffect(() => {
    const fetchSkillCategories = async () => {
      try {
        setIsLoading(true);
        const response = await getSkillCategories(profileData?.profile_id);
        setSkillCategories(response);
      } catch (error) {
        console.error('Error fetching skill categories:', error);
        setFormError('Failed to load skills. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSkillCategories();
  }, []);

      useEffect(() => {
        setCompletion("extra", skillCategories);
      }, [skillCategories])

  // Clear form data
  const clearForm = () => {
    setSkillForm({
      category_name: '',
      skills: ''
    });
    setFormError('');
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSkillForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Add new skill category
  const handleAddSkillCategory = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!skillForm.category_name || !skillForm.skills) {
      setFormError('Please fill out all fields');
      return;
    }
    
    try {
      // First create the skill category
      const categoryData = {
        category_name: skillForm.category_name
      };
      
      const category = await createSkillCategory(categoryData, profileData?.profile_id);
      
      // Then add the individual skills to the category
      const skillsArray = skillForm.skills.split(',').map(skill => skill.trim()).filter(skill => skill);
      
      // Create a new array for updated skills as they are created
      const createdSkills = [];
      
      // Add each skill to the category
      for (const skillName of skillsArray) {
        const skillData = {
          skill_name: skillName,
          proficiency: null // Optional, can be adjusted if needed
        };
        
        const skill = await addSkill(category.id, skillData, profileData?.profile_id);
        createdSkills.push(skill);
      }
      
      // Update the state with the new category and its skills
      setSkillCategories(prev => [...prev, {
        ...category,
        skills: createdSkills
      }]);
      
      // Reset form and state
      clearForm();
      setIsAdding(false);
    } catch (error) {
      console.error('Error adding skill category:', error);
      setFormError('Failed to add skill category. Please try again.');
    }
  };

  // Start editing a skill category
  const handleEditStart = (skillCategory) => {
    setIsEditing(skillCategory.id);
    
    // Format skills for the form
    const skillsText = skillCategory.skills?.map(skill => skill.skill_name).join(', ');
    
    setSkillForm({
      category_name: skillCategory.category_name,
      skills: skillsText
    });
  };

  // Save edited skill category
  const handleEditSave = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!skillForm.category_name || !skillForm.skills) {
      setFormError('Please fill out all fields');
      return;
    }
    
    try {
      // Update the category name
      const categoryData = {
        category_name: skillForm.category_name
      };
      
      const updatedCategory = await updateSkillCategory(isEditing, categoryData, profileData?.profile_id);
      
      // Get the existing category to compare skills
      const existingCategory = skillCategories.find(cat => cat.id === isEditing);
      const existingSkills = existingCategory?.skills || [];
      
      // Parse the new skills from the form
      const newSkillsText = skillForm.skills.split(',').map(skill => skill.trim()).filter(skill => skill);
      
      // Find skills to delete
      for (const existingSkill of existingSkills) {
        if (!newSkillsText.includes(existingSkill.skill_name)) {
          // This skill was removed
          await deleteSkill(existingSkill.id, profileData?.profile_id);
        }
      }
      
      // Find skills to add
      const existingSkillNames = existingSkills.map(skill => skill.skill_name);
      const skillsToAdd = newSkillsText.filter(skillName => !existingSkillNames.includes(skillName));
      
      // Add new skills
      for (const skillName of skillsToAdd) {
        await addSkill(isEditing, { skill_name: skillName }, profileData?.profile_id);
      }
      
      // Fetch updated skill categories to get the complete updated state
      const updatedCategories = await getSkillCategories(profileData?.profile_id);
      setSkillCategories(updatedCategories);
      
      // Reset form and state
      clearForm();
      setIsEditing(null);
    } catch (error) {
      console.error('Error updating skill category:', error);
      setFormError('Failed to update skill category. Please try again.');
    }
  };

  // Cancel adding/editing
  const handleCancel = () => {
    clearForm();
    setIsAdding(false);
    setIsEditing(null);
  };

  // Delete skill category
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this skill category?')) {
      try {
        await deleteSkillCategory(id, profileData?.profile_id);
        setSkillCategories(prev => prev.filter(category => category.id !== id));
      } catch (error) {
        console.error('Error deleting skill category:', error);
        setFormError('Failed to delete skill category. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="skills-loading">
        <div className="loading-spinner"></div>
        <p>Loading skills...</p>
      </div>
    );
  }

  return (
    <div className="skills-section">
      <div className="section-header">
        <h2><div className="section-icon">
          {profileSection.icon}
        </div> Skills</h2>
        {!isAdding && !isEditing && (
          <button 
            onClick={() => setIsAdding(true)}
            className="btn-add"
          >
            <FaPlus /> Add Skill Category
          </button>
        )}
      </div>
      
      {/* Skills Categories List */}
      {skillCategories.length > 0 ? (
        <div className="skills-list-skills">
          {skillCategories.map((category) => (
            <div key={category.id} className="skill-category">
              {isEditing === category.id ? (
                <form onSubmit={handleEditSave} className="skill-form">
                  <h3>Edit Skill Category</h3>
                  {formError && <div className="form-error">{formError}</div>}
                  
                  <div className="form-group">
                    <label htmlFor="category_name">Category Name *</label>
                    <input
                      type="text"
                      id="category_name"
                      name="category_name"
                      value={skillForm.category_name}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                      placeholder="e.g., Programming Languages, Design Tools, Soft Skills"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="skills">Skills *</label>
                    <textarea
                      id="skills"
                      name="skills"
                      value={skillForm.skills}
                      onChange={handleInputChange}
                      rows="4"
                      required
                      className="form-control"
                      placeholder="Enter skills separated by commas (e.g., JavaScript, React, Node.js)"
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="btn-secondary">
                      <FaTimes /> Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      <FaSave /> Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="skill-display">
                  <div className="skill-header">
                    <h3>{category.category_name}</h3>
                    <div className="skill-actions">
                      <button 
                        onClick={() => handleEditStart(category)}
                        className="btn-icon edit"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={() => handleDelete(category.id)}
                        className="btn-icon delete"
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                  
                  <div className="skill-tags">
                    {category.skills?.map((skill) => (
                      <span key={skill.id} className="skill-tag">
                        {skill.skill_name}
                        {skill.proficiency && <span className="skill-proficiency">({skill.proficiency})</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-list">
          <p>No skills added yet.</p>
          <p>Click "Add Skill Category" to get started.</p>
        </div>
      )}
      
      {/* Add Skill Category Form */}
      {isAdding && (
        <div className="add-skill">
          <form onSubmit={handleAddSkillCategory} className="skill-form">
            <h3>Add New Skill Category</h3>
            {formError && <div className="form-error">{formError}</div>}
            
            <div className="form-group">
              <label htmlFor="category_name">Category Name *</label>
              <input
                type="text"
                id="category_name"
                name="category_name"
                value={skillForm.category_name}
                onChange={handleInputChange}
                required
                className="form-control"
                placeholder="e.g., Programming Languages, Design Tools, Soft Skills"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="skills">Skills *</label>
              <textarea
                id="skills"
                name="skills"
                value={skillForm.skills}
                onChange={handleInputChange}
                rows="4"
                required
                className="form-control"
                placeholder="Enter skills separated by commas (e.g., JavaScript, React, Node.js)"
              />
            </div>
            
            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-secondary">
                <FaTimes /> Cancel
              </button>
              <button type="submit" className="btn-primary">
                <FaPlus /> Add Skill Category
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Skills;